/**
 * Retry Logic with Exponential Backoff
 * Handles network failures gracefully with automatic retries
 */

import { API_CONFIG } from '../config';
import { ApiError, NetworkError, logError } from './errorHandler';

/**
 * Sleep utility for delays
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay
 * @param {number} attempt - Current attempt number (0-indexed)
 * @param {number} baseDelay - Base delay in ms (default 1000ms)
 * @returns {number} - Delay in milliseconds
 */
const calculateBackoff = (attempt, baseDelay = 1000) => {
  // Exponential backoff: 1s, 2s, 4s, 8s, etc.
  const delay = Math.min(baseDelay * Math.pow(2, attempt), 10000); // Max 10s

  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 0.3 * delay;

  return delay + jitter;
};

/**
 * Check if error is retryable
 * @param {Error} error - The error to check
 * @param {number} statusCode - HTTP status code
 * @returns {boolean}
 */
const isRetryableError = (error, statusCode) => {
  // Network errors are retryable
  if (error.name === 'NetworkError' || error.name === 'TypeError') {
    return true;
  }

  // Timeout errors are retryable
  if (error.name === 'AbortError') {
    return true;
  }

  // HTTP status codes that are retryable
  if (statusCode) {
    // 5xx server errors are retryable
    if (statusCode >= 500 && statusCode < 600) {
      return true;
    }

    // 429 Too Many Requests is retryable
    if (statusCode === 429) {
      return true;
    }

    // 408 Request Timeout is retryable
    if (statusCode === 408) {
      return true;
    }
  }

  return false;
};

/**
 * Fetch with retry logic and exponential backoff
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} maxRetries - Maximum number of retries
 * @param {number} timeout - Request timeout in ms
 * @returns {Promise<Response>}
 */
export const fetchWithRetry = async (
  url,
  options = {},
  maxRetries = API_CONFIG.MAX_RETRIES,
  timeout = API_CONFIG.TIMEOUT
) => {
  let lastError;
  let response;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // Make the request
      response = await fetch(url, {
        ...options,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Check if response is ok
      if (response.ok) {
        // Success! Log attempt if retried
        if (attempt > 0) {
          console.info(`Request succeeded on attempt ${attempt + 1}`);
        }
        return response;
      }

      // Non-OK response
      const statusCode = response.status;
      lastError = new ApiError(
        `HTTP ${statusCode}: ${response.statusText}`,
        statusCode,
        { url, attempt }
      );

      // Check if we should retry this error
      if (!isRetryableError(lastError, statusCode)) {
        console.warn(`Non-retryable error (${statusCode}), failing immediately`);
        throw lastError;
      }

    } catch (error) {
      // Network or timeout error
      if (error.name === 'AbortError') {
        lastError = new NetworkError('Request timed out', { url, timeout, attempt });
      } else if (error instanceof ApiError) {
        lastError = error;
      } else {
        lastError = new NetworkError(
          error.message || 'Network request failed',
          { url, attempt, originalError: error.name }
        );
      }

      // Check if offline
      if (!navigator.onLine) {
        console.warn('Device is offline, stopping retries');
        throw new NetworkError('Device is offline', { url });
      }

      // Check if retryable
      if (!isRetryableError(lastError, lastError.statusCode)) {
        throw lastError;
      }
    }

    // If we get here, we need to retry
    if (attempt < maxRetries) {
      const backoffDelay = calculateBackoff(attempt);
      console.warn(
        `Request failed (attempt ${attempt + 1}/${maxRetries + 1}), ` +
        `retrying in ${Math.round(backoffDelay)}ms...`,
        lastError
      );
      await sleep(backoffDelay);
    }
  }

  // All retries exhausted
  console.error(`All ${maxRetries + 1} attempts failed for ${url}`);
  logError(lastError, { url, maxRetries, timeout });
  throw lastError;
};

/**
 * Wrapper for GET requests with retry
 * @param {string} url - URL to fetch
 * @param {Object} headers - Request headers
 * @param {Object} options - Additional options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const getWithRetry = async (url, headers = {}, options = {}) => {
  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...headers
    },
    ...options
  });

  return response.json();
};

/**
 * Wrapper for POST requests with retry
 * @param {string} url - URL to fetch
 * @param {Object} data - Request body
 * @param {Object} headers - Request headers
 * @param {Object} options - Additional options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const postWithRetry = async (url, data, headers = {}, options = {}) => {
  const response = await fetchWithRetry(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers
    },
    body: JSON.stringify(data),
    ...options
  });

  return response.json();
};

/**
 * Circuit breaker pattern for preventing cascade failures
 */
export class CircuitBreaker {
  constructor(threshold = 5, timeout = 60000) {
    this.failureCount = 0;
    this.threshold = threshold; // Number of failures before opening circuit
    this.timeout = timeout; // Time before attempting to close circuit (ms)
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.nextAttempt = Date.now();
  }

  async execute(fn) {
    if (this.state === 'OPEN') {
      if (Date.now() < this.nextAttempt) {
        throw new Error('Circuit breaker is OPEN');
      }
      // Try half-open state
      this.state = 'HALF_OPEN';
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  onSuccess() {
    this.failureCount = 0;
    this.state = 'CLOSED';
  }

  onFailure() {
    this.failureCount++;
    if (this.failureCount >= this.threshold) {
      this.state = 'OPEN';
      this.nextAttempt = Date.now() + this.timeout;
      console.warn(`Circuit breaker opened after ${this.failureCount} failures`);
    }
  }

  reset() {
    this.failureCount = 0;
    this.state = 'CLOSED';
    this.nextAttempt = Date.now();
  }

  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      nextAttempt: this.nextAttempt
    };
  }
}

// Create singleton circuit breaker for API requests
export const apiCircuitBreaker = new CircuitBreaker(5, 60000);
